import { computed, ComputedRef, provide, ref, Ref, SetupFunction, watch } from 'vue';
import { trans, Trans } from '@/domain/dao/Translations';

type Configuration = {
	prompt?: string;
	contexts: string[];
	triggers: string[];
	aiAgentId: string;
};

type MultiSelectOption = {
	id: string;
	name: string;
	value: string;
};

type AiAgentOption = {
	id: string;
	name: string;
};

type Props = {
	fieldName: string;
	configuration: Configuration;
	contextOptions: string[];
	triggerOptions: string[];
	aiAgentOptions: AiAgentOption[];
};

type View = {
	lang: Trans;
	prompt: Ref<string>;
	aiAgentConfigurationString: ComputedRef<string>;
	contextOptions: MultiSelectOption[];
	triggerOptions: MultiSelectOption[];
	aiAgentOptions: AiAgentOption[];
	selectedContexts: string[];
	selectedTriggers: string[];
	selectedAiAgent: string;
	updateConfiguration: (type: string, keys: string[]) => void;
	updateAiAgentConfigValue: (name: string, selected: string) => void;
	testPrompt: () => void;
};

const aiAgentConfiguratorController: SetupFunction<Props, View> = (
	props,
	{ emit }: { emit: (event: string, data?: string) => void }
) => {
	const lang = trans();
	provide('lang', lang);

	const defaultConfiguration: Configuration = {
		prompt: '',
		contexts: [],
		triggers: [],
		aiAgentId: '',
	};

	const aiAgentConfiguration: Ref<Configuration> = ref({
		...defaultConfiguration,
		...props.configuration,
	} as Configuration);

	const prompt = computed({
		get: () => aiAgentConfiguration.value.prompt || '',
		set: (value: string) => (aiAgentConfiguration.value.prompt = value),
	});

	const aiAgentConfigurationString = computed(() => {
		const config = { ...aiAgentConfiguration.value };

		if (!config.prompt) {
			delete config.prompt;
		}

		return JSON.stringify(config);
	});

	const forMultiSelectOptions = <T extends Record<string, string[]>, K extends { [P in keyof T]: string }>(
		options: T,
		translationKeys: K
	): { [P in keyof T]: MultiSelectOption[] } =>
		Object.fromEntries(
			Object.entries(options).map(([key, optionList]) => [
				key,
				optionList.map((option) => ({
					id: option,
					name: lang.get(`${translationKeys[key as keyof T]}.${option}`),
					value: option,
				})),
			])
		) as { [P in keyof T]: MultiSelectOption[] };

	const { contextOptions, triggerOptions } = forMultiSelectOptions(
		{
			contextOptions: props.contextOptions,
			triggerOptions: props.triggerOptions,
		},
		{
			contextOptions: 'fields.contexts',
			triggerOptions: 'fields.triggers',
		}
	);

	const updateConfiguration = (type: string, keys: string[]) => {
		if (['contexts', 'triggers'].includes(type)) {
			aiAgentConfiguration.value = {
				...aiAgentConfiguration.value,
				[type]: keys,
			};
		}
	};

	const updateAiAgentConfigValue = (name: string, selected: string) => {
		aiAgentConfiguration.value = {
			...aiAgentConfiguration.value,
			aiAgentId: selected,
		};
	};

	watch(
		() => aiAgentConfigurationString.value,
		(newValue) => {
			emit('updated', newValue);
		},
		{ immediate: true }
	);

	return {
		lang,
		prompt,
		aiAgentConfigurationString,
		contextOptions,
		triggerOptions,
		selectedContexts: aiAgentConfiguration.value.contexts,
		selectedTriggers: aiAgentConfiguration.value.triggers,
		updateConfiguration,
		aiAgentOptions: props.aiAgentOptions,
		selectedAiAgent: aiAgentConfiguration.value.aiAgentId || props.aiAgentOptions[0]?.id || '',
		updateAiAgentConfigValue,
		testPrompt: () => {},
	};
};

export { aiAgentConfiguratorController, Props, View };
